/**
 * User Profile Page (F074)
 *
 * Accessible to ALL authenticated users (no admin gate).
 * Shows user profile, security status, and personal preferences.
 *
 * Route: /profile | Nav: User menu dropdown
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ShieldCheckmarkRegular,
  KeyRegular,
} from '@fluentui/react-icons';
import { UserProfileCard } from '../features/identity';
import { useAuth } from '../features/authentication';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';

export const ProfilePage: React.FC = () => {
  const ps = usePageStyles();
  const { user } = useAuth();

  const roles = user?.roles ?? [];
  const memberships = user?.tenantMemberships ?? [];

  return (
    <div className={ps.page}>
      <PageHeader
        title="Your Profile"
        subtitle="View your account details and security status"
        icon={<PersonRegular />}
      />

      {/* Profile Card */}
      <UserProfileCard />

      <div className={ps.twoColumn}>
        {/* Security Status */}
        <Card>
          <CardHeader
            image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />}
            header={<Title2>Security</Title2>}
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              <div className={ps.rowItem}>
                <Body1>Authentication</Body1>
                <Badge appearance="filled" color="success">Azure AD SSO</Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>MFA Status</Body1>
                <Badge appearance="filled" color="success">Enabled via Azure AD</Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Session</Body1>
                <Badge appearance="tint" color="informative">Active</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Roles & Access */}
        <Card>
          <CardHeader
            image={<KeyRegular style={{ fontSize: '1.5rem', color: '#8852E0' }} />}
            header={<Title2>Roles & Access</Title2>}
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              <div className={ps.rowItem}>
                <Body1>Assigned Roles</Body1>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                  {roles.length > 0 ? roles.map(role => (
                    <Badge key={role} appearance="tint" color="informative">{role}</Badge>
                  )) : (
                    <Badge appearance="tint" color="warning">No roles assigned</Badge>
                  )}
                </div>
              </div>
              <div className={ps.rowItem}>
                <Body1>Tenant Memberships</Body1>
                <Body2>{memberships.length} organization{memberships.length !== 1 ? 's' : ''}</Body2>
              </div>
              {memberships.map(m => (
                <div key={m.tenantId} className={ps.rowItem}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{m.tenantName || m.tenantId}</Body2>
                  <Badge appearance="outline" size="small">{m.role || 'Member'}</Badge>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
